import React, { useEffect, useState, useRef } from 'react';

/**
 * SVGPathAnimator Component
 * 
 * Displays an SVG file path by path with animation control
 * 
 * @param {Object} props
 * @param {string} props.url - URL of the SVG file to display
 * @param {number} props.start - Delay in seconds before animation starts (default: 0)
 * @param {number} props.duration - Duration in seconds for the entire animation (default: 1)
 * @param {boolean} props.loop - Whether animation should loop (default: false)
 * @param {string|number} props.strokeWidth - Override stroke width for all paths (optional)
 */
export const SVGPathAnimator = ({ url, start = 0, duration = 1, loop = false, strokeWidth }) => {
  const [svgContent, setSvgContent] = useState(null);
  const [paths, setPaths] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);

  // Fetch the SVG file
  useEffect(() => {
    const fetchSVG = async () => {
      try {
        const response = await fetch(url);
        const text = await response.text();
        setSvgContent(text);
      } catch (error) {
        console.error('Error fetching SVG:', error);
      }
    };

    if (url) {
      fetchSVG();
    }
  }, [url]);

  // Parse SVG content to extract paths
  useEffect(() => {
    if (!svgContent) return;

    // Create a temporary DOM element to parse the SVG
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    
    // Extract the SVG element to get viewBox and dimensions
    const svgElement = svgDoc.querySelector('svg');
    if (!svgElement) return;

    // Get all path elements
    const pathElements = Array.from(svgDoc.querySelectorAll('path'));
    
    // Store path data
    const pathsData = pathElements.map(path => ({
      d: path.getAttribute('d'),
      fill: path.getAttribute('fill') || 'none',
      stroke: path.getAttribute('stroke') || '#000',
      strokeWidth: path.getAttribute('stroke-width') || '1',
      strokeLinecap: path.getAttribute('stroke-linecap') || 'butt',
      strokeLinejoin: path.getAttribute('stroke-linejoin') || 'miter',
      style: path.getAttribute('style') || '',
      visibility: 'hidden' // Initially hidden
    }));

    // Store viewBox and other SVG attributes
    const viewBox = svgElement.getAttribute('viewBox');
    const width = svgElement.getAttribute('width');
    const height = svgElement.getAttribute('height');

    setPaths({
      pathsData,
      viewBox,
      width,
      height
    });
  }, [svgContent]);

  // Setup Intersection Observer to detect when component is visible
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(containerRef.current);

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  // Handle animation start when component becomes visible
  useEffect(() => {
    if (!isVisible || !paths.pathsData || paths.pathsData.length === 0) return;

    const startDelay = start * 1000; // Convert to milliseconds
    
    const timer = setTimeout(() => {
      startAnimation();
    }, startDelay);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(animationRef.current);
    };
  }, [isVisible, paths, start, duration]);

  const startAnimation = () => {
    startTimeRef.current = performance.now();
    animatePaths();
  };

  const animatePaths = () => {
    const currentTime = performance.now();
    const elapsedTime = currentTime - startTimeRef.current;
    const totalDuration = duration * 1000; // Convert to milliseconds
    
    if (elapsedTime >= totalDuration) {
      // Animation complete
      if (loop) {
        // Restart animation
        startTimeRef.current = performance.now();
        animationRef.current = requestAnimationFrame(animatePaths);
      }
      return;
    }
    
    // Calculate how many paths should be visible
    const progress = elapsedTime / totalDuration;
    const visiblePathCount = Math.ceil(progress * paths.pathsData.length);
    
    // Update paths visibility
    const updatedPathsData = paths.pathsData.map((path, index) => ({
      ...path,
      visibility: index < visiblePathCount ? 'visible' : 'hidden'
    }));
    
    setPaths(prevPaths => ({
      ...prevPaths,
      pathsData: updatedPathsData
    }));
    
    animationRef.current = requestAnimationFrame(animatePaths);
  };

  // Clean up animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  if (!paths.pathsData) {
    return <div>Loading SVG...</div>;
  }

  return (
    <div ref={containerRef}>
      <svg
        viewBox={paths.viewBox}
        width={paths.width}
        height={paths.height}
      >
        {paths.pathsData.map((path, index) => (
          <path
            key={index}
            d={path.d}
            fill={path.fill}
            stroke={path.stroke}
            strokeWidth={strokeWidth || path.strokeWidth}
            strokeLinecap={path.strokeLinecap}
            strokeLinejoin={path.strokeLinejoin}
            style={{ ...path.style, visibility: path.visibility }}
          />
        ))}
      </svg>
    </div>
  );
};

export default SVGPathAnimator;